import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  User,
  Mail,
  Shield,
  ShieldCheck,
  ShieldAlert,
  Settings,
  ChevronRight,
} from "lucide-react";

export default function Profile() {
  const { user, logout } = useAuth();

  return (
    <div className="min-h-screen p-4 md:p-8">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-heading font-bold mb-2">Profile</h1>
          <p className="text-muted-foreground">Manage your account settings</p>
        </motion.div>

        {/* Profile Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-card rounded-2xl p-8 mb-6"
        >
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-2xl bg-primary/20 flex items-center justify-center neon-glow">
              <User className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h2 className="text-2xl font-heading font-bold">{user?.username}</h2>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Mail className="w-4 h-4" />
                <span>{user?.email}</span>
              </div>
            </div>
            {user?.is_admin && (
              <Badge className="ml-auto bg-secondary text-secondary-foreground">
                Admin
              </Badge>
            )}
          </div>

          <div className="grid gap-4">
            <div className="flex items-center justify-between p-4 rounded-lg bg-background/50 border border-white/5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                  <User className="w-5 h-5 text-muted-foreground" />
                </div>
                <div>
                  <div className="font-medium">Username</div>
                  <div className="text-sm text-muted-foreground">{user?.username}</div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between p-4 rounded-lg bg-background/50 border border-white/5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                  <Mail className="w-5 h-5 text-muted-foreground" />
                </div>
                <div>
                  <div className="font-medium">Email</div>
                  <div className="text-sm text-muted-foreground">{user?.email}</div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Security Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass-card rounded-2xl p-6 mb-6"
        >
          <h3 className="font-heading font-bold text-lg mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" /> Security
          </h3>

          <Link to="/setup-2fa">
            <div className="flex items-center justify-between p-4 rounded-lg bg-background/50 border border-white/5 hover:border-primary/30 transition-colors cursor-pointer">
              <div className="flex items-center gap-3">
                <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    user?.totp_enabled ? "bg-primary/20" : "bg-yellow-500/20"
                  }`}
                >
                  {user?.totp_enabled ? (
                    <ShieldCheck className="w-5 h-5 text-primary" />
                  ) : (
                    <ShieldAlert className="w-5 h-5 text-yellow-500" />
                  )}
                </div>
                <div>
                  <div className="font-medium">Two-Factor Authentication</div>
                  <div className="text-sm text-muted-foreground">
                    {user?.totp_enabled
                      ? "Enabled with Google Authenticator"
                      : "Not enabled - Secure your account"}
                  </div>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </div>
          </Link>
        </motion.div>

        {/* Admin Link */}
        {user?.is_admin && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="glass-card rounded-2xl p-6 mb-6"
          >
            <Link to="/admin">
              <div className="flex items-center justify-between p-4 rounded-lg bg-secondary/10 border border-secondary/30 hover:border-secondary transition-colors cursor-pointer">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-secondary/20 flex items-center justify-center">
                    <Settings className="w-5 h-5 text-secondary" />
                  </div>
                  <div>
                    <div className="font-medium text-secondary">Admin Dashboard</div>
                    <div className="text-sm text-muted-foreground">
                      Manage users, transactions, and fraud alerts
                    </div>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-secondary" />
              </div>
            </Link>
          </motion.div>
        )}

        {/* Logout */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Button
            variant="outline"
            onClick={logout}
            className="w-full border-destructive/50 text-destructive hover:bg-destructive/10"
            data-testid="logout-btn"
          >
            Sign Out
          </Button>
        </motion.div>
      </div>
    </div>
  );
}
